import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useSearchParams } from 'react-router'
import { gql } from 'graphql-request'
import Layout from '@/components/Layout/Layout'
import { Tabs } from '@/components/Common/Tabs'
import { useStores } from '@/components/StoreProvider'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { MessageSquare, BarChart3, LayoutDashboard } from 'lucide-react'
import ChatsTab from '@/pages/Dashboard/components/ChatsTab'
import UserTargetsTab from '@/pages/Dashboard/components/UserTargetsTab'
import OverviewTab from './components/OverviewTab'
import type { OrganisationWithDetails } from '@/pages/Dashboard'

const GET_ORGANISATION_DETAILS = gql`
  query getOrganisationsWithSubscriptions($organisationId: Int) {
    getOrganisationsWithSubscriptions(organisationId: $organisationId) {
      id
      name
      description
      isActive
      createdAt
      updatedAt
      users {
        id
        email
        firstName
        lastName
        isActive
        createdAt
        updatedAt
      }
      activeSubscriptions {
        id
        organisationId
        moduleId
        module {
          id
          name
        }
        numUsers
        numTokens
        numSessions
        numMinutes
        dueDate
        active
        createdAt
        updatedAt
      }
      lastInactiveSubscription {
        id
        organisationId
        moduleId
        module {
          id
          name
        }
        numUsers
        numTokens
        numSessions
        numMinutes
        dueDate
        active
        createdAt
        updatedAt
      }
    }
  }
`

const CoachPage: React.FC = observer(() => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { userStore } = useStores()
  const [orgDetails, setOrgDetails] = useState<OrganisationWithDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<string>(searchParams.get('tab') || 'overview')

  // Redirect non-coaches
  useEffect(() => {
    if (!userStore.isCoach) {
      navigate('/')
    }
  }, [userStore.isCoach, navigate])

  // Fetch coach's organisation details
  useEffect(() => {
    const fetchOrgDetails = async () => {
      const orgId = userStore.user?.organization_id
      if (!orgId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await authorizedGraphQLRequest<{
          getOrganisationsWithSubscriptions: OrganisationWithDetails[]
        }>(GET_ORGANISATION_DETAILS, { organisationId: orgId })

        if (response.getOrganisationsWithSubscriptions?.length > 0) {
          setOrgDetails(response.getOrganisationsWithSubscriptions[0])
        }
      } catch (error) {
        console.error('Failed to fetch organisation details:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrgDetails()
  }, [userStore.user?.organization_id])

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setSearchParams((prev) => {
      prev.set('tab', tab)
      return prev
    })
  }

  if (!userStore.isCoach) return null

  const tabs = [
    {
      value: 'overview',
      label: 'Overview',
      icon: <LayoutDashboard className="h-4 w-4" />,
      content: <OverviewTab organisationId={userStore.user?.organization_id ?? null} organisationName={orgDetails?.name} />,
    },
    {
      value: 'chats',
      label: 'Chats',
      icon: <MessageSquare className="h-4 w-4" />,
      content: <ChatsTab organisation={orgDetails} loading={loading} from="coach" />,
    },
    {
      value: 'user-targets',
      label: 'User Targets',
      icon: <BarChart3 className="h-4 w-4" />,
      content: <UserTargetsTab organisation={orgDetails} loading={loading} />,
    },
  ]

  return (
    <Layout withHeader={true}>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="p-6">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-800">Coach Dashboard</h1>
              {orgDetails && <p className="text-gray-500 mt-1">{orgDetails.name}</p>}
            </div>

            {loading && !orgDetails ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : !userStore.user?.organization_id ? (
              <div className="text-center text-gray-500 py-12">
                <p>You are not assigned to any organisation.</p>
              </div>
            ) : (
              <Tabs tabs={tabs} defaultValue={activeTab} onValueChange={handleTabChange} />
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
})

export default CoachPage
